import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { noop } from 'lodash-es';
import moment from 'moment';
import { filter, finalize, firstValueFrom, fromEvent, map, switchMap, take, tap } from 'rxjs';
import { ServerRootService } from '../../../lib/utils/routing/serverroot.service';
import { CancellationError } from '../../confirm/confirm-dialog.service';
import { RadioSettingsService } from '../../services/settings/radio-settings.service';
import { ITokenResponse } from '../../services/authentication/authentication.service';
import { SignType } from '../../models/estensasign.model';
import {
    SISS_CLOUD_LOCALSTORAGE_CODE_KEY,
    SISS_CLOUD_LOCALSTORAGE_ERROR_KEY,
    SISS_CLOUD_LOCALSTORAGE_STATE_KEY,
    SISS_CLOUD_LOCALSTORAGE_TOKEN_KEY,
    SissCloudAuthToken,
    SissCloudInvalidTokenError,
} from './siss-cloud.models';
import { buildQueryString, ChildWindowBuilderService, WindowTarget } from '../../services/embedding/child.service';
import { Siss3Settings, SissWay3Settings } from 'estensa-module';
import {
    EstensaBusWrapperService,
    ISissWayTokenMessage,
    isSissWayTokenRequestMessage,
} from '../../services/angularjs/estensa-bus-wrapper.service';
import { CacheService } from '../../utils/cache.service';

export interface SsoState {
    done: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class SissCloudTokenService {
    token: SissCloudAuthToken | null = null;

    constructor(
        private cache: CacheService,
        private childWindow: ChildWindowBuilderService,
        private http: HttpClient,
        private serverRoot: ServerRootService,
        private settings: RadioSettingsService,
        private busService: EstensaBusWrapperService,
    ) {
        /*ascolta il bus service per fornire sissway-token al client angularjs*/
        this.busService.busDataFired.subscribe(async (msg) => {
            if (isSissWayTokenRequestMessage(msg)) {
                await this.handleHtml5SissWayTokenRequest(msg.userId);
            }
        });

    }

    async handleHtml5SissWayTokenRequest(userId: string) {
        try {
            const token = await this.getAccessToken(userId);
            const message: ISissWayTokenMessage = {
                messageType: 'ESTENSA_SISSWAY_TOKEN',
                token,
            };
            this.busService.notify(message);
        } catch (err) {
            console.error('SissCloudTokenService.handleHtml5SissWayTokenRequest error', err);
            const message: ISissWayTokenMessage = {
                messageType: 'ESTENSA_SISSWAY_TOKEN',
                token: null,
            };
            this.busService.notify(message);
        }
    }

    test = async () => {
        this.cache.set<SissWay3Settings>('SissWay3Settings', {
            clientSecret: 'test_secret',
            consumerKey: 'test_consumer_key',
            codBrancaSpecialistica: '10',
            idStruttura: '000',
            idSubStruttura: '000',
            redirectURL: '/test/sissway-redirect-url',
            xsissWayURL: 'http://localhost:43210/sissway',
        });
        // de-commentare per bypassare il login durante i test in localhost
        // this.token = {
        //     access_token: '',
        //     expirationDate: moment().add(2, 'hour'),
        //     expires_in: 10**6,
        //     lastValidDate: moment().add(1, 'hour'),
        //     refreshTimeout: 10**6,
        //     refresh_token: '',
        //     token_type: '',
        //     userId:'d7c1d1e6-f770-47e6-94ad-2bdd7454f05a'
        // }
    };

    getServerRootUrl() {
        /* return 'test' per testare con server in locale ed angular proxy*/
        return this.serverRoot.getServerRoot();
    }

    safeExit() {
        clearTimeout(this.token?.refreshTimeout);
        this.token = null;
    }

    isTokenRefreshable() {
        return !!this.token && this.token.lastValidDate.clone().subtract(1, 'minute').isAfter(Date.now());
    }

    isTokenValid(userId: string) {
        return (
            !!this.token &&
            this.token.userId === userId &&
            this.isTokenRefreshable() &&
            this.token.expirationDate.clone().subtract(1, 'minute').isAfter(Date.now())
        );
    }

    async getSiss3Settings() {
        return firstValueFrom(
            this.cache.get(
                'Siss3Settings',
                this.http.get<Siss3Settings>(
                    `${this.getServerRootUrl()}/Siss3/Settings`,
                    this.serverRoot.buildOptions(),
                ),
            ),
        );
    }

    async getSissWay3Settings() {
        return firstValueFrom(
            this.cache.get(
                'SissWay3Settings',
                this.http.get<SissWay3Settings>(
                    `${this.getServerRootUrl()}/SissWay3/Settings`,
                    this.serverRoot.buildOptions(),
                ),
            ),
        );
    }

    async getAccessToken(userId: string) {
        if (!this.isTokenValid(userId)) {
            await this.requestNewToken(userId);
        }
        if (!this.token) {
            throw new SissCloudInvalidTokenError();
        }

        return this.token.access_token;
    }

    async getFullTokenData(userId: string) {
        if (!this.isTokenValid(userId)) {
            await this.requestNewToken(userId);
        }
        if (!this.token) {
            throw new SissCloudInvalidTokenError();
        }

        return this.token;
    }

    async logout(): Promise<void> {
        if (!this.token) {
            return;
        }
        await firstValueFrom(
            this.http
                .delete(
                    `${this.getServerRootUrl()}/Siss3/Logout?accessToken=${this.token?.access_token}`,
                    this.serverRoot.buildOptions(null, true),
                )
                .pipe(
                    finalize(() => {
                        // Pulisci il token anche in caso di errore. Potrebbe comunque non essere più valido.
                        this.safeExit();
                    }),
                ),
        );
    }

    private async requestNewToken(userId: string) {
        // Caso felice in cui l'utente è lo stesso del token, il refresh token è ancora valido e la sessione non è
        //   scaduta.
        if (this.token && this.token.userId === userId && this.isTokenRefreshable()) {
            if (await this.refreshToken()) {
                return;
            }
        }

        // Se l'utente è cambiato, è necessario fare prima il logout.
        if (this.token && this.token.userId !== userId) {
            await this.logout();
        }

        // Se l'utente è lo stesso, ma il token non è più refreshabile, effettua nuovamente il login.
        this.safeExit();
        const settings = await firstValueFrom(this.settings.radioSettingsAsync);
        if (settings.reportSettings.signType === SignType.SISSCloud) {
            await this.requestNewTokenFromSissCloud(userId);
        } else {
            await this.requestNewTokenFromSissWayCloud(userId);
        }
    }

    /**
     * Flusso OAuth2 con authorization code.
     * [esempio](https://datatracker.ietf.org/doc/html/rfc6749#section-4.1)
     * - Aprire pagina di SSO dalla quale l'utente farà login.
     * - La pagina di SSO reindirizza alla nostra pagina di callback siss-cloud-callback.html.
     * - La pagina di callback segnala tramite localStorage che ha ricevuto l'authorization code.
     * - L'authorization code viene scambiato per un access token e refresh token.
     */
    private async requestNewTokenFromSissCloud(userId: string) {
        const { apiManagerURL, cardOnly, consumerKey, redirectURL, tokenExpHours } = await this.getSiss3Settings();

        const urlParams = {
            response_type: 'code',
            client_id: consumerKey,
            redirect_uri: redirectURL,
            scope: 'siss_sa siss_sa_sso',
            state: this.generateRandomState(),
            friendlyName: 'SISSOperatore',
            cardOnly: cardOnly ? 'true' : 'false',
            prompt: 'login',
        };

        return new Promise<string | null>((resolve, reject) => {
            // Questo stato viene condiviso dal flusso di localstorage e della finestra di autenticazione.
            // Quando la finestra riceve il messaggio che l'utente ha finito l'autenticazione, il campo {done} viene
            //   impostato a true, cosicché il loop che controlla se la finestra è stata chiusa possa sapere se il
            //   flusso è andato a buon fine o è stato cancellato.
            const ssoState: SsoState = { done: false };
            // Questo viene controllato dalla pagina di redirect intermedia. Se non corrisponde, l'autenticazione non è
            //   andata a buon fine.
            localStorage.setItem(SISS_CLOUD_LOCALSTORAGE_STATE_KEY, urlParams.state);
            // Quando la pagina intermedia setta l'authorization code...
            const storageSubscription = this.listenForSsoReply(
                SISS_CLOUD_LOCALSTORAGE_CODE_KEY,
                SISS_CLOUD_LOCALSTORAGE_ERROR_KEY,
                ssoState,
            )
                .pipe(
                    switchMap((code) => {
                        // ...scambia il code con un access token...
                        return this.http.get<ITokenResponse>(
                            `${this.getServerRootUrl()}/Siss3/Token?code=${code}`,
                            this.serverRoot.buildOptions(),
                        );
                    }),
                    tap((tokenResponse) => {
                        // ...quindi salvalo e inizia il loop di refresh.
                        this.setupToken(tokenResponse, tokenExpHours, userId);
                    }),
                )
                .subscribe({
                    next: (tokenResponse) => {
                        // Questo risolve la promise esterna, e quindi la funzione.
                        resolve(tokenResponse.access_token);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });

            // Finestra di autenticazione sul SISS.
            // Alla sua chiusura viene fatto l'unsubcribe dal localStorage.
            this.openLoginWindow(`${apiManagerURL}/oauth2/authorize`, urlParams, ssoState)
                .then(() => {
                    storageSubscription?.unsubscribe();
                })
                .catch((err) => {
                    storageSubscription?.unsubscribe();
                    reject(err);
                });
        });
    }

    /**
     * Flusso "OAuth2" con client credentials.
     * [esempio](https://datatracker.ietf.org/doc/html/rfc6749#section-4.4)
     * - Aprire pagina di SSO dalla quale l'utente farà login.
     * - La pagina di SSO reindirizza alla nostra pagina di callback siss-cloud-callback.html.
     * - La pagina di callback segnala tramite localStorage che ha ricevuto l'access token.
     */
    private async requestNewTokenFromSissWayCloud(userId: string) {
        const { clientSecret, consumerKey, redirectURL, xsissWayURL } = await this.getSissWay3Settings();
        const { tokenExpHours } = await this.getSiss3Settings();

        const state = this.generateRandomState();
        const urlParams = {
            Authorization: btoa(`${consumerKey}:${clientSecret}`),
            redirectUrl: `${redirectURL}?${buildQueryString({ state })}`,
        };

        // Vedi la corrispondente funzione per il SissCloud per una spiegazione sul funzionamento.
        // La differenza in questo flusso è che l'XSiss-Way ritorna direttamente un access_token, invece che un
        //   authorization code.
        return new Promise<string | null>((resolve, reject) => {
            localStorage.setItem(SISS_CLOUD_LOCALSTORAGE_STATE_KEY, state);

            const ssoState: SsoState = { done: false };

            const storageSubscription = this.listenForSsoReply(
                SISS_CLOUD_LOCALSTORAGE_TOKEN_KEY,
                SISS_CLOUD_LOCALSTORAGE_ERROR_KEY,
                ssoState,
            ).subscribe({
                next: (tokenResponse) => {
                    if (!tokenResponse) {
                        return reject(new Error('SissCloudInvalidSsoResponse'));
                    }

                    this.setupToken(
                        {
                            access_token: tokenResponse,
                            expires_in: tokenExpHours * 60 * 60,
                            refresh_token: '',
                            token_type: 'bearer',
                        },
                        8,
                        userId,
                    );
                    resolve(tokenResponse);
                },
                error: (err) => {
                    reject(err);
                },
            });

            this.openLoginWindow(`${xsissWayURL}/sissway-api/login`, urlParams, ssoState)
                .then(() => {
                    storageSubscription?.unsubscribe();
                })
                .catch((err) => {
                    storageSubscription?.unsubscribe();
                    reject(err);
                });
        });
    }

    /**
     * Ritorna un observable che emette un valore quando viene impostato un elemento con chiave {successKey} in
     * localStorage, oppure emette un errore quando viene impostato un elemento con chiave {errorKey}.
     */
    private listenForSsoReply(successKey: string, errorKey: string, ssoState: SsoState) {
        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter((event) => event.key === successKey || event.key === errorKey),
            tap(() => {
                localStorage.removeItem(successKey);
                localStorage.removeItem(errorKey);
                ssoState.done = true;
            }),
            map((event) => {
                if (event.key === errorKey) {
                    throw new Error('SISSUnauthorized');
                }
                return event.newValue;
            }),
            take(1),
        );
    }

    private generateRandomState() {
        const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

        let state = '';
        for (let i = 0; i < 8; ++i) {
            const index = Math.floor(Math.random() * alphabet.length);
            state += alphabet[index];
        }

        return `estensaweb${state}`;
    }

    private async openLoginWindow(url: string, params: Record<string, string>, ssoState: SsoState) {
        return this.childWindow
            .withUrl(url, params)
            .withTarget(WindowTarget.Popup)
            .withSize(1024, 768)
            .open()
            .then((ssoWindow) => {
                // Controlla ogni secondo se la finestra è stata chiusa.
                // Se la promise non è ancora risolta, l'utente ha probabilmente cancellato il flusso.
                return firstValueFrom(ssoWindow.closed$().pipe(filter((closed) => closed)));
            })
            .then(() => {
                if (!ssoState.done) {
                    throw new CancellationError();
                }
            });
    }

    private setupToken(token: ITokenResponse, tokenExpHours: number, userId: string) {
        this.safeExit();

        const expiresIn = moment.duration(token.expires_in, 'seconds');

        this.token = {
            ...token,
            expirationDate: moment().add(expiresIn),
            refreshTimeout: setTimeout(
                () => this.refreshToken(),
                expiresIn.clone().subtract(moment.duration(5, 'minutes')).asMilliseconds(),
            ),
            lastValidDate: moment().add(tokenExpHours, 'hours'),
            userId,
        };
    }

    private async refreshToken() {
        if (!this.token) {
            return false;
        }

        const { tokenExpHours } = await this.getSiss3Settings();

        const urlParams = {
            refreshToken: this.token.refresh_token,
        };

        try {
            const newToken = await firstValueFrom(
                this.http.get<ITokenResponse>(
                    `${this.getServerRootUrl()}/Siss3/RefreshToken?${buildQueryString(urlParams)}`,
                ),
            );

            this.setupToken(newToken, tokenExpHours, this.token.userId);
            return true;
        } catch {
            // In caso il refresh non vada a buon fine, fai il logout ignrando gli errori, è possibile che non funzioni
            //   neanche quello.
            await this.logout().catch(noop);
            return false;
        }
    }
}
